import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Send, Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "I'm restless — what would actually help?",
  "I'm tired but want to feel alive",
  "I'm new in town — make this easy",
  "I want to make new friends",
  "Why do you ask about my mood?",
];

const INITIAL_GREETING = "Hey 👋 Tell me one thing about how you're feeling right now — and I'll match it to a real-world activity that fits. Where do you want to start?";

export default function ConciergeChat({ embedded = false }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: INITIAL_GREETING },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  const resetToStart = () => {
    setMessages([{ role: "assistant", content: INITIAL_GREETING }]);
    setSessionId(null);
    setInput("");
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/concierge/chat`, {
        session_id: sessionId,
        message: msg,
      });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Hmm, I couldn't connect just now. Try again in a sec — I've got brunch crews and trivia nights queued up for you.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className={`bg-surface border border-line rounded-4xl shadow-xl overflow-hidden flex flex-col ${
        embedded ? "h-[560px]" : "h-[520px]"
      }`}
      data-testid="concierge-chat-widget"
    >
      <div className="px-5 py-4 border-b border-line flex items-center gap-3 bg-surface-alt">
        <div className="w-9 h-9 rounded-xl bg-primary text-white flex items-center justify-center">
          <Sparkles size={18} />
        </div>
        <div>
          <div className="font-display font-black text-lg leading-none">Activity Concierge</div>
          <div className="text-xs text-muted mt-0.5">Powered by AI · Live now</div>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-muted">
          <span className="w-2 h-2 rounded-full bg-green-500 pulse-dot" /> Online
        </span>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-3" data-testid="concierge-messages">
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} text={m.content} />
        ))}
        {sending && <Bubble role="assistant" text="…" typing />}

        {messages.length === 1 && !sending && (
          <div className="pt-2 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-xs font-medium px-3 py-2 rounded-full border border-line bg-white hover:border-primary hover:text-primary transition-colors"
                data-testid={`concierge-suggestion-${s.slice(0, 10).replace(/\s+/g, "-").toLowerCase()}`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {messages.length > 2 && !sending && (
          <div className="pt-1 flex justify-center">
            <button
              type="button"
              onClick={resetToStart}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-full border border-line bg-white text-muted hover:text-primary hover:border-primary transition-colors"
              data-testid="concierge-reset"
            >
              ← Back to main choices
            </button>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="p-3 border-t border-line bg-white flex items-center gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Tell me how you're feeling..."
          className="flex-1 px-4 py-3 rounded-2xl bg-surface-alt border border-transparent focus:border-primary focus:outline-none text-sm"
          data-testid="concierge-input"
          disabled={sending}
        />
        <button
          type="submit"
          disabled={sending || !input.trim()}
          className="w-12 h-12 rounded-2xl bg-primary text-white flex items-center justify-center disabled:opacity-50 hover:bg-primary-600 transition-colors"
          data-testid="concierge-send-button"
          aria-label="Send"
        >
          <Send size={18} />
        </button>
      </form>
    </div>
  );
}

function Bubble({ role, text, typing }) {
  const isUser = role === "user";
  // Strip leftover markdown (asterisks, lone # at line starts, hyphen/+ list prefixes
  // that the LLM may still produce despite instructions). Convert any "- " or "+ " or
  // "* " bullet prefixes to a clean "• " glyph for consistent rendering.
  const clean = !isUser && text
    ? text
        .replace(/\*\*(.+?)\*\*/g, "$1")          // **bold** → bold
        .replace(/\*(.+?)\*/g, "$1")              // *italic* → italic
        .replace(/^#+\s+/gm, "")                  // line-leading # heading
        .replace(/^\s*[-+*]\s+/gm, "• ")          // - + * bullets → •
    : text;
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-ink text-white rounded-br-md"
            : "bg-surface-alt text-ink rounded-bl-md"
        }`}
      >
        {typing ? (
          <span className="inline-flex gap-1 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-muted animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-muted animate-bounce" style={{ animationDelay: "120ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-muted animate-bounce" style={{ animationDelay: "240ms" }} />
          </span>
        ) : (
          clean
        )}
      </div>
    </div>
  );
}

// Floating launcher version (used in corner)
export function ConciergeFloating() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 bg-ink text-white px-5 py-3.5 rounded-full shadow-2xl flex items-center gap-2 hover:bg-primary transition-colors btn-shimmer"
        data-testid="concierge-floating-button"
      >
        <Sparkles size={18} />
        <span className="font-semibold text-sm">Ask the Concierge</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6" onClick={() => setOpen(false)}>
          <div className="w-full md:w-[460px] max-w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2 hidden md:flex">
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-full bg-white shadow flex items-center justify-center"
                data-testid="concierge-close-button"
              >
                <X size={16} />
              </button>
            </div>
            <ConciergeChat />
          </div>
        </div>
      )}
    </>
  );
}
